import APIWrapper from '../utils/graphqlwrapper';
import logger from '../utils/logger';

class SubscriptionService {
  constructor() {
    this.activeSubscriptions = new Map();
  }

  async startSumBySourceSubscription(executionId, clientId) {
    /**
     * Starts a subscription for Sum By Source export results (downloads)
     * @param {string} executionId The ID of the Sum By Source execution
     * @param {string} clientId The client ID associated with the execution
     * @returns {Object} The subscription object
     */
    const subscriptionKey = `sbs-${executionId}`;

    if (this.activeSubscriptions.has(subscriptionKey)) {
      logger.info('Subscription already active for execution:', executionId);
      return this.activeSubscriptions.get(subscriptionKey);
    }

    try {
      logger.info('Starting SBS export subscription for execution:', executionId);

      const subscriptionObservable = await APIWrapper.subscribeApi(
        { subscription: 'onUpdateSumBySourceExportResults' },
        false,
        {
          filter: { id: { eq: executionId } },
        },
      );

      const subscription = subscriptionObservable.subscribe({
        next: (response) => {
          logger.info('SBS export subscription update received for:', executionId);

          try {
            let responseData = null;

            if (response && response.data) {
              responseData = response.data.onUpdateSumBySourceExportResults;
            }

            if (responseData) {
              const { status } = responseData;
              window.dispatchEvent(new CustomEvent('sbsStatusUpdate', {
                detail: {
                  executionId,
                  clientId,
                  status,
                  data: responseData,
                },
              }));

              if (status === 'Success' || status === 'Failed') {
                logger.info('SBS export job completed, unsubscribing...');
                this.unsubscribe(subscriptionKey);
              }
            }
          } catch (err) {
            logger.error('Error processing SBS export subscription response:', err.message);
          }
        },
        error: (error) => {
          logger.error('SBS export subscription error:', error);
          this.unsubscribe(subscriptionKey);
        },
      });

      // Store the subscription
      const subscriptionInfo = {
        subscription,
        executionId,
        clientId,
        startTime: Date.now(),
      };

      this.activeSubscriptions.set(subscriptionKey, subscriptionInfo);
      logger.info('SBS subscription started and stored:', subscriptionKey);

      return subscriptionInfo;
    } catch (err) {
      logger.error('Failed to start SBS subscription:', err);
      throw err;
    }
  }

  async startSumBySourceDisplaySubscription(clientId) {
    /**
     * Starts a subscription for Sum By Source display cache results
     * @param {string} clientId The client ID to subscribe to
     * @returns {Object} The subscription object
     */
    const subscriptionKey = `sbs-display-${clientId}`;

    if (this.activeSubscriptions.has(subscriptionKey)) {
      logger.info('Display subscription already active for client:', clientId);
      return this.activeSubscriptions.get(subscriptionKey);
    }

    try {
      logger.info('Starting SBS display subscription for client:', clientId);

      const subscriptionObservable = await APIWrapper.subscribeApi(
        { subscription: 'onUpdateSumBySourceDisplayCache' },
        false,
        {
          filter: { client_id: { eq: clientId } },
        },
      );

      const subscription = subscriptionObservable.subscribe({
        next: (response) => {
          logger.info('SBS display subscription update received for:', clientId);

          try {
            let responseData = null;

            if (response && response.data) {
              responseData = response.data.onUpdateSumBySourceDisplayCache;
            }

            if (responseData) {
              const { status } = responseData;
              window.dispatchEvent(new CustomEvent('sbsDisplayUpdate', {
                detail: {
                  clientId,
                  status,
                  data: responseData,
                },
              }));

              if (status === 'Success' || status === 'Failed') {
                logger.info('SBS display job completed, unsubscribing...');
                this.unsubscribe(subscriptionKey);
              }
            }
          } catch (err) {
            logger.error('Error processing SBS display subscription response:', err.message);
          }
        },
        error: (error) => {
          logger.error('SBS display subscription error:', error);
          this.unsubscribe(subscriptionKey);
        },
      });

      const subscriptionInfo = {
        subscription,
        clientId,
        startTime: Date.now(),
      };

      this.activeSubscriptions.set(subscriptionKey, subscriptionInfo);
      logger.info('SBS display subscription started:', subscriptionKey);

      return subscriptionInfo;
    } catch (err) {
      logger.error('Failed to start SBS display subscription:', err);
      throw err;
    }
  }

  unsubscribe(subscriptionKey) {
    /**
     * Unsubscribes from a given subscription
     * @param {string} subscriptionKey The key of the subscription to unsubscribe from
     */
    const subscriptionInfo = this.activeSubscriptions.get(subscriptionKey);
    if (subscriptionInfo) {
      logger.info('Unsubscribing from:', subscriptionKey);
      subscriptionInfo.subscription.unsubscribe();
      this.activeSubscriptions.delete(subscriptionKey);
      logger.info('Successfully unsubscribed from:', subscriptionKey);
    } else {
      logger.warn('No active subscription found for:', subscriptionKey);
    }
  }

  unsubscribeAll() {
    /**
     * Unsubscribes from all active subscriptions
     */
    logger.info('Unsubscribing from all active subscriptions...');
    this.activeSubscriptions.forEach((_, key) => {
      this.unsubscribe(key);
    });
  }

  getActiveSubscriptions() {
    /**
     * Returns a list of active subscriptions
     * @returns {Array} List of active subscription info
     */
    return Array.from(this.activeSubscriptions.entries()).map(([key, info]) => ({
      key,
      executionId: info.executionId,
      clientId: info.clientId,
      startTime: info.startTime,
    }));
  }

  isSubscriptionActive(executionId) {
    /**
     * Checks if a subscription is active for a given execution ID
     * @param {string} executionId The execution ID to check
     * @returns {boolean} True if active, false otherwise
     */
    return this.activeSubscriptions.has(`sbs-${executionId}`);
  }
}

// Create an instance
const subscriptionService = new SubscriptionService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    subscriptionService.unsubscribeAll();
  });
}

export default subscriptionService;
